// @file creates URL object

import { currentCountryIso31662 } from '@@/bits/current_locale'
import environment from '@@/bits/environment'
import overwrite_host from '@@/bits/overwrite_host'
import { HAS_URL_REGEX, WEB_URL_REGEX } from '@@/bits/regex'
import { appendTaUtmParams, type TaUtmContext } from '@@/bits/ta'
import punycode from 'punycode/'

const isUrl = (str: string | null): boolean => {
  if (str === null) return false
  return WEB_URL_REGEX.test(str)
}

const hasUrl = (str: string): boolean => {
  return HAS_URL_REGEX.test(str)
}

const addHttpsIfAbsent = (maybeUrl: string): string => {
  try {
    const url = new URL(maybeUrl)
    return url.toString()
  } catch {
    // Invalid URL but it could still be a URL
    // -> conveniently add https:// since user might not type it.
    const newUrl = `https://${maybeUrl}`
    return isUrl(newUrl) ? newUrl : maybeUrl
  }
}

const PROD_HOSTS = {
  native: 'padlet.com',
  org: 'padlet.org',
}
const STAGING_HOSTS = {
  native: 'padlet.io',
  org: 'padlet.org',
}
const DEV_HOSTS = {
  native: 'padlet.dev',
  org: 'padletorg.dev',
}
const TEST_HOSTS = {
  native: 'padlet.test',
  org: 'padletorg.test',
}
const PADLET_HOSTS = {
  production: PROD_HOSTS,
  staging: STAGING_HOSTS,
  development: DEV_HOSTS,
  test: TEST_HOSTS,
  e2e_test: TEST_HOSTS,
  e2e_ci: TEST_HOSTS,
}

let NATIVE_HOST: string
let ORG_HOST: string
if (environment !== 'development') {
  NATIVE_HOST = PADLET_HOSTS[environment].native
  ORG_HOST = PADLET_HOSTS[environment].org
} else {
  NATIVE_HOST = overwrite_host || PADLET_HOSTS[environment].native
  ORG_HOST = overwrite_host || PADLET_HOSTS[environment].org
}

// Hosts that may legitimately serve Area 51 per environment. Mirrors
// `Loki::AREA51_HOSTS` on the Rails side. The first entry is the canonical
// host, used as a fallback when `window.location.host` isn't in the list
// (prod/staging only — other envs trust whatever host the page loaded from
// since codespaces and trycloudflare tunnels can't be fully enumerated).
//
// Drift caveat: there is no CI guardrail keeping this in sync with
// `Loki::AREA51_HOSTS`. Same shape, same risk, as the pre-existing
// `PADLET_HOSTS` (here) / `Loki#native_hosts` pair. Single-source-of-truth
// (bootstrap the list from the server into the page) is a separate cleanup.
const AREA51_HOSTS: Record<string, string[]> = {
  production: ['padlet.com', 'prod.wallwisherinfra.net'],
  staging: ['padlet.io', 'staging.wallwisherinfra.net'],
  development: ['padlet.dev'],
  test: ['padlet.test'],
  e2e_test: ['padlet.test'],
  e2e_ci: ['padlet.test'],
}
const AREA51_ALLOWLIST_ENFORCED_ENVS = ['production', 'staging']

// Returns the host serving Area 51 for this page. In prod/staging, returns
// `window.location.host` if allowlisted, otherwise the canonical host.
// Elsewhere, returns `window.location.host` as-is.
const area51Host = (): string => {
  const allowed = AREA51_HOSTS[environment]
  const host = window.location.host
  // Guard for a misconfigured env (env in AREA51_ALLOWLIST_ENFORCED_ENVS but
  // missing from AREA51_HOSTS). Matches the Rails-side fetch-with-fallback
  // shape. In practice both maps are static + reviewed together; this just
  // prevents `allowed.includes(...)` from throwing on undefined.
  if (!allowed) return host
  if (!AREA51_ALLOWLIST_ENFORCED_ENVS.includes(environment)) return host
  return allowed.includes(host) ? host : allowed[0]
}

function internalUrlRegexString(): string {
  return (
    '^' +
    // Allow protocol to be omitted
    '(https:\\/\\/)?' +
    // Allow subdomain to be omitted
    `([a-z0-9\\-]+\\.)?` +
    // Allow `padlet` and `padletorg` domain names
    `(padlet|padletorg)\\.` +
    // Allow `com`, `org`, `io`, `test` and `dev` TLDs
    `(com|org|io|test|dev)`
  )
}

function profileUrlRegexString(): string {
  return (
    internalUrlRegexString() +
    // Profile name
    '\\/[\\w_]{1,64}' +
    // Optional trailing slash, query params, and hash
    '[\\/]?(\\?[^#]*)?(#.*)?$'
  )
}

function wallUrlRegexString(): string {
  return (
    internalUrlRegexString() +
    // Namespace
    '\\/[a-zA-Z0-9][a-zA-Z0-9_]{1,64}' +
    // Wall name
    '\\/[a-zA-Z0-9_\\-]+' +
    // Optional trailing slash, query params, and hash
    '[\\/]?(\\?[^#]*)?(#.*)?$'
  )
}

function wallWishUrlRegexString(): string {
  return (
    internalUrlRegexString() +
    // Namespace
    '\\/[a-zA-Z0-9][a-zA-Z0-9_]{1,64}' +
    // Wall name
    '\\/[a-zA-Z0-9_\\-]+' +
    // Wish subpath
    '\\/wish' +
    // Wish hashid
    '\\/[\\w]+' +
    // Optional trailing slash, query params, and hash
    '[\\/]?(\\?[^#]*)?(#.*)?$'
  )
}

function sectionBreakoutRegexString(): string {
  return (
    internalUrlRegexString() +
    // Namespace
    '\\/[a-zA-Z0-9][a-zA-Z0-9_]{1,64}' +
    // breakout-link OR breakout-room ONLY
    '\\/(?:breakout-link|breakout-room)' +
    // sectionHash-shareLinkHash
    '\\/[a-zA-Z0-9]+-[a-zA-Z0-9]+' +
    // Optional wish subpath
    '(\\/wish\\/[\\w]+)?' +
    // Optional trailing slash, query params, and hash
    '[\\/]?(\\?[^#]*)?(#.*)?$'
  )
}

function frameBreakoutRegexString(): string {
  return (
    internalUrlRegexString() +
    // Namespace
    '\\/[a-zA-Z0-9][a-zA-Z0-9_]{1,64}' +
    // card-breakout-link / card-breakout-room / (legacy) canvas-breakout-link
    '\\/(?:card-breakout-link|card-breakout-room|canvas-breakout-link)' +
    // Identifier: {wallHashid}-page:{cardId}
    '\\/[\\w\\-]+-page:[\\w\\-]+' +
    // Optional trailing slash, query params, and hash
    '[\\/]?(\\?[^#]*)?(#.*)?$'
  )
}

function submissionRequestRegexString(): string {
  return (
    internalUrlRegexString() +
    // Namespace
    '\\/[a-zA-Z0-9][a-zA-Z0-9_]{1,64}' +
    // submission-request
    '\\/submission-request' +
    // shareLinkHash
    '\\/[a-zA-Z0-9]+' +
    // Optional trailing slash, query params, and hash
    '[\\/]?(\\?[^#]*)?(#.*)?$'
  )
}

function slideshowUrlRegexString(): string {
  return (
    internalUrlRegexString() +
    // Namespace
    '\\/[a-zA-Z0-9][a-zA-Z0-9_]{1,64}' +
    // Wall name
    '\\/[a-zA-Z0-9_\\-]+' +
    // Slideshow subpath
    '\\/slideshow' +
    // Optional: either wish subpath or section subpath
    '(\\/wish\\/[\\w]+|\\/section\\/[\\w]+)?' +
    // Optional trailing slash, query params, and hash
    '[\\/]?(\\?[^#]*)?(#.*)?$'
  )
}

function makePageUrlRegexString(): string {
  return (
    internalUrlRegexString() +
    // Dashboard path with optional library slug
    '\/dashboard(?:\/[^/]+)*' +
    // Make path with optional trailing slash, query params and hash
    '\/make[\\/]?(\\?[^#]*)?(#.*)?$'
  )
}

function isValidWallUrlFormat(url): boolean {
  return !(RegExp(wallUrlRegexString()).exec(url) == null)
}

function isWallWishUrlFormat(url): boolean {
  return !(RegExp(wallWishUrlRegexString()).exec(url) == null)
}

function isWallOrWallWishUrlFormat(url): boolean {
  return isValidWallUrlFormat(url) || isWallWishUrlFormat(url)
}

function isProfileUrlFormat(url: string): boolean {
  return !(RegExp(profileUrlRegexString()).exec(url) == null)
}

function isSlideshowUrlFormat(url: string): boolean {
  return !(RegExp(slideshowUrlRegexString()).exec(url) == null)
}

function isSubmissionRequestUrlFormat(url: string): boolean {
  return !(RegExp(submissionRequestRegexString()).exec(url) == null)
}

function isBreakoutUrlFormat(url: string): boolean {
  return RegExp(sectionBreakoutRegexString()).exec(url) != null || RegExp(frameBreakoutRegexString()).exec(url) != null
}

// Padlet-owned hosts across all environments, derived from PADLET_HOSTS so the
// allow-list stays in sync as environments change.
const INTERNAL_URL_HOSTS = [...new Set(Object.values(PADLET_HOSTS).flatMap(({ native, org }) => [native, org]))]

// True only when `url` is an absolute http(s) URL whose host is a Padlet host
// (exact match or a subdomain). The host must terminate the label boundary, so
// `https://padlet.com.attacker.com` is rejected. Non-absolute inputs (bare
// hosts, protocol-relative `//evil.com`, relative paths) and non-http schemes
// (e.g. javascript:) return false, so callers must pass a full URL.
function isInternalUrl(url: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return false
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false
  const host = parsed.hostname.toLowerCase()
  return INTERNAL_URL_HOSTS.some((root) => host === root || host.endsWith(`.${root}`))
}

function isMakePageUrlFormat(url: string): boolean {
  return !(RegExp(makePageUrlRegexString()).exec(url) == null)
}

const getHostname = (str: string): string => {
  const url: URL = new URL(str)
  return url.hostname
}

const getPathWithLeadingSlash = (str: string): string => {
  let pathWithLeadingSlash = ''
  if (str.startsWith('/')) {
    pathWithLeadingSlash = str
  } else if (str.match(/^https?:/) !== null) {
    try {
      const url: URL = new URL(str)
      pathWithLeadingSlash = url.pathname + url.search + url.hash
    } catch {
      console.warn('Not a valid URL', str)
      pathWithLeadingSlash = str.replace(/^https?:/, '')
    }
  } else {
    pathWithLeadingSlash = '/' + str
  }
  return pathWithLeadingSlash
}

const addQueryParams = (url: string | null, paramsString: string): string => {
  if (url == null || url === '') return '?'.concat(paramsString)
  if (url.match(/\?/) != null) {
    return [url, paramsString].join('&')
  }
  return [url, paramsString].join('?')
}

const selfHarmHelplineUrl = (): string => {
  // Have to check for "XX" because that's what the backend returns for unknown country
  return `https://padlet.findahelpline.com/${currentCountryIso31662 === 'XX' ? '' : currentCountryIso31662}`
}

const getDemoUrl = (
  variant: 'external' | 'default' = 'default',
  config: { pageName?: string; linkLocation?: string },
): string => {
  const { pageName, linkLocation } = config
  const utmMedium = 'cta'
  const utmSource = pageName != null && pageName.length > 0 ? `padlet_${pageName}` : 'padlet'
  const utmCampaign = linkLocation != null && linkLocation.length > 0 ? `padlet_${linkLocation}` : 'padlet'
  // We use a redirect rules on Cloudflare to redirect to the correct URL depends on the variant to try out different ways to improve conversion rate
  // https://dash.cloudflare.com/cc33bc6b9fa2dcffb7db4d7877260139/padlet.com/rules/redirect-rules/faf7d000f94a46f584fab79ff9fe3599
  return `https://padlet.com/premium/backpack/try?variant=${variant}&utm_medium=${utmMedium}&utm_source=${utmSource}&utm_campaign=${utmCampaign}`
}

function goTo(url: string): void {
  location.replace(url)
}

const isValidUrl = (url: string): boolean => {
  try {
    return Boolean(new URL(url))
  } catch {
    return false
  }
}

/**
 * Checks if the given string is a valid URL and decodes it if
 * it's URL encoded.
 * @param url The URL to check.
 * @returns true if the URL is valid and safe, false otherwise.
 */
const validateExternalUrl = (url: string): boolean => {
  if (url === '') {
    return true
  }
  // Left-trim the url to avoid spaces
  url = url.replace(/^\s+/, '')
  // Ensure not percent encoded URL
  url = /^https?%3A/.test(url) ? decodeURIComponent(url) : url
  // Make sure the URL starts with http or https, instead of e.g. javascript: or mailto:
  if (!/^https?/.test(url) || !isValidUrl(url)) {
    return false
  }
  // If we get here, the URL is valid
  return true
}

/**
 * Sanitizes a URL by ensuring it only uses http/https protocols to prevent XSS attacks.
 * Decodes any encoded URL and validates the protocol.
 * @param url The URL string to sanitize
 * @param base Optional base to resolve root-relative paths (e.g. `/foo`) against. Absolute
 *   URLs (incl. `javascript:`/`data:`) ignore the base, so the scheme allow-list still holds.
 * @returns The sanitized URL string if valid, null if the URL uses a disallowed protocol
 */
const sanitizeUrl = (url: string, base?: string): string | null => {
  if (url === '') return null
  try {
    url = url.replace(/\(/g, '%28').replace(/\)/g, '%29')
    const urlObject = new URL(url, base)
    if (urlObject.protocol !== 'http:' && urlObject.protocol !== 'https:') {
      return null
    }
    return urlObject.href
  } catch {
    return null
  }
}

/**
 * converts a url in punycode format (xn--pdlet-mra.com) to unicode format (pådlet.com)
 * this function accepts url strings with or without protocol
 */
const convertPunycodeToUnicodeUrl = (url: string): string => {
  if (!url.includes('xn--')) return url
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
    const unicodeHostname = punycode.toUnicode(urlObj.hostname)
    return url.startsWith('http') ? url.replace(urlObj.hostname, unicodeHostname) : unicodeHostname
  } catch {
    return url
  }
}

const isImageUrl = async (url: string): Promise<boolean> => {
  if (!url || typeof url !== 'string') {
    return false
  }

  return await new Promise<boolean>((resolve) => {
    const image = new Image()
    const cleanup = (): void => {
      image.onload = null
      image.onerror = null
      image.src = ''
    }
    image.onload = () => {
      cleanup()
      resolve(true)
    }
    image.onerror = () => {
      cleanup()
      resolve(false)
    }
    image.src = url
  })
}

/**
 * Creates an arcade embed URL by ensuring it has ?embed=1 parameter
 * and removing /play suffix if present
 * @param url The arcade URL to transform
 * @param options Optional analytics attribution options.
 * @param options.source Optional source identifier appended as ?source=<value> for analytics attribution
 * @param options.utmContext Optional TA utm context. When provided, the arcade `utm_*` params for that
 *   context are appended too (attributing the embed load to PostHog with the right utm_medium).
 *   Coexists with `source`; omitted by default so existing callers keep their current behavior.
 * @returns The transformed URL with ?embed=1 parameter
 */
const createArcadeEmbedUrl = (url: string, options?: { source?: string; utmContext?: TaUtmContext }): string => {
  const { source, utmContext } = options ?? {}
  try {
    const urlObj = new URL(url)

    // Remove trailing slash from pathname if present
    if (urlObj.pathname.endsWith('/')) {
      urlObj.pathname = urlObj.pathname.slice(0, -1)
    }

    // Remove /play suffix if present
    if (urlObj.pathname.endsWith('/play')) {
      urlObj.pathname = urlObj.pathname.slice(0, -5) // Remove '/play'
    }

    // Check if embed=1 is already in the query params
    if (urlObj.searchParams.get('embed') !== '1') {
      urlObj.searchParams.set('embed', '1')
    }

    if (source != null) {
      urlObj.searchParams.set('source', source)
    }

    // appendTaUtmParams merges into the existing query, so embed=1 and source above are preserved.
    return utmContext != null ? appendTaUtmParams(urlObj.toString(), utmContext) : urlObj.toString()
  } catch {
    // If URL parsing fails, return original URL
    return url
  }
}

// Convert string to slug format
// Based on lib/core_ext/string.rb's slugify method which uses parameterize
// https://apidock.com/rails/v7.1.3.4/ActiveSupport/Inflector/parameterize
const slugify = (str: string, options: { truncate: boolean } = { truncate: false }): string => {
  if (str == null || str === '') return ''
  let slug = str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '-') // Replace non-alphanumeric chars with hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
  if (options.truncate) {
    slug = slug.substring(0, 60)
    // Remove any trailing hyphens that may have been created by truncation to prevent malformed URLs
    slug = slug.replace(/-+$/g, '')
  }
  return slug
}

export {
  addHttpsIfAbsent,
  addQueryParams,
  area51Host,
  convertPunycodeToUnicodeUrl,
  createArcadeEmbedUrl,
  getDemoUrl,
  getHostname,
  getPathWithLeadingSlash,
  goTo,
  hasUrl,
  isBreakoutUrlFormat,
  isImageUrl,
  isInternalUrl,
  isMakePageUrlFormat,
  isProfileUrlFormat,
  isSlideshowUrlFormat,
  isSubmissionRequestUrlFormat,
  isUrl,
  isValidWallUrlFormat,
  isWallOrWallWishUrlFormat,
  isWallWishUrlFormat,
  NATIVE_HOST,
  ORG_HOST,
  sanitizeUrl,
  selfHarmHelplineUrl,
  slugify,
  validateExternalUrl,
}
